import { FC } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import Stack from "@mui/material/Stack";
import { ExportMetrics as ExportMetricsType } from "../../../types/niffler";

interface MetricCardProps {
  title: string;
  description: string;
  value: number | string;
  loading?: boolean;
}

interface SectionCardProps {
  children: React.ReactNode;
  padding?: number;
  flexGrow?: boolean;
}

const SectionCard = ({
  children,
  padding,
  flexGrow = false,
}: SectionCardProps) => {
  return (
    <Stack
      border={1}
      borderColor="divider"
      borderRadius={1}
      padding={padding}
      flexDirection="column"
      sx={{
        width: {
          xs: "100%", // Mobile: full width
          sm: "100%", // Small: full width
          md: "calc(30% - 4px)", // Medium: 2 per row (minus gap)
          lg: 300, // Large: fixed 300px (4 per row)
        },
        ...(flexGrow && {
          flexGrow: {
            xs: 0, // No flexGrow on mobile
            sm: 0, // No flexGrow on small
            md: 0, // No flexGrow on medium
            lg: 1, // FlexGrow only on large screens
          },
          minWidth: {
            md: 320, // Minimum width only needed on lg
          },
        }),
      }}
    >
      {children}
    </Stack>
  );
};

const MetricCard: FC<MetricCardProps> = ({
  title,
  description,
  value,
  loading = false,
}) => (
  <SectionCard padding={3}>
    <Stack direction="column" justifyContent="space-between" gap={1}>
      <Typography variant="h2" color="text.secondary">
        {title}
      </Typography>

      <Stack direction="column" gap={0.5}>
        {loading ? (
          <Skeleton variant="rounded" width={40} height={32} />
        ) : (
          <Typography
            variant="h1"
            fontSize={32}
            fontWeight="bold"
            color="#090909"
          >
            {value}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Stack>
    </Stack>
  </SectionCard>
);

interface ExportMetricsProps {
  stats: ExportMetricsType | null;
  loading: boolean;
}

export const ExportMetrics: FC<ExportMetricsProps> = ({ stats, loading }) => {
  return (
    <Stack direction="row" flexWrap="wrap" gap={1} mb={3}>
      {/* Metric Cards */}
      <MetricCard
        title="Completed"
        description="Exports"
        value={stats?.totalExportsCompleted ?? 0}
        loading={loading}
      />

      <MetricCard
        title="Processing"
        description="Exports"
        value={stats?.totalExportsProcessing ?? 0}
        loading={loading}
      />

      <MetricCard
        title="Total Exports"
        description="Records"
        value={stats?.totalExports ?? 0}
        loading={loading}
      />

      {/* Information Card with flexGrow */}
      <SectionCard padding={2} flexGrow>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <InfoOutlineIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="body2" component="h3" color="text.secondary">
            About your data exports.
          </Typography>
        </Stack>

        <Box marginLeft={3}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            This page shows the status of your data exports.
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={400}>
            Track completed, processing, or failed exports, and download your
            data in CSV or XLS format.
          </Typography>
        </Box>
      </SectionCard>
    </Stack>
  );
};
